import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Textarea, Select } from '@/components/ui'
import { Globe, Copy, Check, RefreshCw, Eye } from 'lucide-react'
import { toast } from 'sonner'

interface MetaData {
  title: string
  description: string
  keywords: string
  author: string
  robots: string
  canonical: string
  ogTitle: string
  ogDescription: string
  ogImage: string
  ogType: string
  ogUrl: string
  twitterCard: string
  twitterTitle: string
  twitterDescription: string
  twitterImage: string
  twitterSite: string
  viewport: string
  charset: string
  themeColor: string
}

export function MetaTags() {
  const [copied, setCopied] = useState(false)
  const [data, setData] = useState<MetaData>({
    title: 'My Awesome Website - Home',
    description: 'Welcome to my awesome website. We provide the best services and products for our customers.',
    keywords: 'website, services, products, awesome',
    author: 'John Doe',
    robots: 'index, follow',
    canonical: 'https://example.com/',
    ogTitle: 'My Awesome Website',
    ogDescription: 'Welcome to my awesome website. We provide the best services and products.',
    ogImage: 'https://example.com/og-image.jpg',
    ogType: 'website',
    ogUrl: 'https://example.com/',
    twitterCard: 'summary_large_image',
    twitterTitle: 'My Awesome Website',
    twitterDescription: 'Welcome to my awesome website. We provide the best services and products.',
    twitterImage: 'https://example.com/twitter-image.jpg',
    twitterSite: '@mywebsite',
    viewport: 'width=device-width, initial-scale=1.0',
    charset: 'UTF-8',
    themeColor: '#80cbc4',
  })

  const updateField = (field: keyof MetaData, value: string) => {
    setData((prev) => ({ ...prev, [field]: value }))
  }

  const generateMetaTags = (): string => {
    const tags: string[] = []

    // Charset & Viewport
    tags.push(`<meta charset="${data.charset}">`)
    tags.push(`<meta name="viewport" content="${data.viewport}">`)

    // Basic Meta
    if (data.title) tags.push(`<title>${data.title}</title>`)
    if (data.description) tags.push(`<meta name="description" content="${data.description}">`)
    if (data.keywords) tags.push(`<meta name="keywords" content="${data.keywords}">`)
    if (data.author) tags.push(`<meta name="author" content="${data.author}">`)
    if (data.robots) tags.push(`<meta name="robots" content="${data.robots}">`)
    if (data.canonical) tags.push(`<link rel="canonical" href="${data.canonical}">`)
    if (data.themeColor) tags.push(`<meta name="theme-color" content="${data.themeColor}">`)

    // Open Graph
    tags.push('')
    tags.push('<!-- Open Graph / Facebook -->')
    if (data.ogType) tags.push(`<meta property="og:type" content="${data.ogType}">`)
    if (data.ogUrl) tags.push(`<meta property="og:url" content="${data.ogUrl}">`)
    if (data.ogTitle) tags.push(`<meta property="og:title" content="${data.ogTitle}">`)
    if (data.ogDescription) tags.push(`<meta property="og:description" content="${data.ogDescription}">`)
    if (data.ogImage) tags.push(`<meta property="og:image" content="${data.ogImage}">`)

    // Twitter
    tags.push('')
    tags.push('<!-- Twitter -->')
    if (data.twitterCard) tags.push(`<meta name="twitter:card" content="${data.twitterCard}">`)
    if (data.twitterSite) tags.push(`<meta name="twitter:site" content="${data.twitterSite}">`)
    if (data.twitterTitle) tags.push(`<meta name="twitter:title" content="${data.twitterTitle}">`)
    if (data.twitterDescription) tags.push(`<meta name="twitter:description" content="${data.twitterDescription}">`)
    if (data.twitterImage) tags.push(`<meta name="twitter:image" content="${data.twitterImage}">`)

    return tags.join('\n')
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateMetaTags())
      setCopied(true)
      toast.success('Meta tags copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const syncOgTwitter = () => {
    setData((prev) => ({
      ...prev,
      ogTitle: prev.title,
      ogDescription: prev.description,
      twitterTitle: prev.title,
      twitterDescription: prev.description,
    }))
    toast.success('Synced title & description to OG and Twitter')
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Globe className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Meta Tags Generator</h1>
            <p className="text-muted-foreground">Generate SEO-optimized meta tags for your website</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_500px]">
        {/* Form */}
        <div className="space-y-4">
          {/* Basic SEO */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Basic SEO</CardTitle>
              <CardDescription>Essential meta tags for search engines</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Page Title</Label>
                <Input
                  value={data.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="My Website - Page Title"
                />
                <p className="text-xs text-muted-foreground">
                  {data.title.length}/60 characters (recommended)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={data.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Brief description of your page..."
                  className="min-h-[80px]"
                />
                <p className="text-xs text-muted-foreground">
                  {data.description.length}/160 characters (recommended)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Keywords</Label>
                  <Input
                    value={data.keywords}
                    onChange={(e) => updateField('keywords', e.target.value)}
                    placeholder="keyword1, keyword2"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Author</Label>
                  <Input
                    value={data.author}
                    onChange={(e) => updateField('author', e.target.value)}
                    placeholder="Author name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Robots</Label>
                  <Select value={data.robots} onChange={(e) => updateField('robots', e.target.value)}>
                    <option value="index, follow">Index, Follow</option>
                    <option value="index, nofollow">Index, No Follow</option>
                    <option value="noindex, follow">No Index, Follow</option>
                    <option value="noindex, nofollow">No Index, No Follow</option>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Theme Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={data.themeColor}
                      onChange={(e) => updateField('themeColor', e.target.value)}
                      className="w-12 h-9 p-1"
                    />
                    <Input
                      value={data.themeColor}
                      onChange={(e) => updateField('themeColor', e.target.value)}
                      className="font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Canonical URL</Label>
                <Input
                  value={data.canonical}
                  onChange={(e) => updateField('canonical', e.target.value)}
                  placeholder="https://example.com/page"
                />
              </div>
            </CardContent>
          </Card>

          {/* Open Graph */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Open Graph (Facebook)</CardTitle>
                  <CardDescription>Social sharing for Facebook, LinkedIn, etc.</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={syncOgTwitter}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Sync from Basic
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>OG Title</Label>
                  <Input
                    value={data.ogTitle}
                    onChange={(e) => updateField('ogTitle', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>OG Type</Label>
                  <Select value={data.ogType} onChange={(e) => updateField('ogType', e.target.value)}>
                    <option value="website">Website</option>
                    <option value="article">Article</option>
                    <option value="product">Product</option>
                    <option value="profile">Profile</option>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>OG Description</Label>
                <Textarea
                  value={data.ogDescription}
                  onChange={(e) => updateField('ogDescription', e.target.value)}
                  className="min-h-[60px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>OG Image URL</Label>
                  <Input
                    value={data.ogImage}
                    onChange={(e) => updateField('ogImage', e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div className="space-y-2">
                  <Label>OG URL</Label>
                  <Input
                    value={data.ogUrl}
                    onChange={(e) => updateField('ogUrl', e.target.value)}
                    placeholder="https://example.com/"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Twitter */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Twitter Card</CardTitle>
              <CardDescription>Social sharing for Twitter/X</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Card Type</Label>
                  <Select value={data.twitterCard} onChange={(e) => updateField('twitterCard', e.target.value)}>
                    <option value="summary">Summary</option>
                    <option value="summary_large_image">Summary Large Image</option>
                    <option value="app">App</option>
                    <option value="player">Player</option>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Twitter @username</Label>
                  <Input
                    value={data.twitterSite}
                    onChange={(e) => updateField('twitterSite', e.target.value)}
                    placeholder="@username"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Twitter Title</Label>
                <Input
                  value={data.twitterTitle}
                  onChange={(e) => updateField('twitterTitle', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Twitter Description</Label>
                <Textarea
                  value={data.twitterDescription}
                  onChange={(e) => updateField('twitterDescription', e.target.value)}
                  className="min-h-[60px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Twitter Image URL</Label>
                <Input
                  value={data.twitterImage}
                  onChange={(e) => updateField('twitterImage', e.target.value)}
                  placeholder="https://example.com/twitter-image.jpg"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview & Output */}
        <div className="space-y-4">
          {/* Google Preview */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Google Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-white text-black rounded-lg">
                <p className="text-blue-800 text-lg hover:underline cursor-pointer">
                  {data.title || 'Page Title'}
                </p>
                <p className="text-green-700 text-sm">
                  {data.canonical || 'https://example.com'}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  {data.description?.slice(0, 160) || 'Page description...'}
                  {data.description?.length > 160 ? '...' : ''}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Social Preview */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Social Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border border-gray-300 rounded-lg overflow-hidden bg-white text-black">
                {data.ogImage && (
                  <div className="aspect-video bg-gray-100 flex items-center justify-center">
                    <img
                      src={data.ogImage}
                      alt="OG Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  </div>
                )}
                <div className="p-3">
                  <p className="text-xs text-gray-500 uppercase">{new URL(data.ogUrl || 'https://example.com').hostname}</p>
                  <p className="font-semibold text-gray-900">{data.ogTitle || 'Title'}</p>
                  <p className="text-sm text-gray-600 line-clamp-2">{data.ogDescription || 'Description'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Generated Code */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Generated Meta Tags</CardTitle>
                <Button onClick={copyToClipboard}>
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  Copy
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <pre className="p-4 bg-muted/40 rounded-lg overflow-x-auto text-xs font-mono whitespace-pre-wrap">
                {generateMetaTags()}
              </pre>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
